import { trackError, ErrorSeverity } from './error-tracking';

/**
 * Localhost origin used ONLY in development/test. It must never be baked into a
 * user-facing URL in production (Stripe return_url, emailed links, embed/product
 * URLs), so `getBaseUrl` refuses to return it when running in production.
 */
export const LOCALHOST_BASE_URL = 'http://localhost:3010';

function stripTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

/**
 * Resolve the site's absolute base URL for building user-facing links and for
 * server-side (SSR/SSG/API) fetches that need an absolute origin.
 *
 * Resolution order:
 *   1. `NEXTAUTH_URL` — the canonical site origin.
 *   2. `VERCEL_URL`   — the deployment origin (prefixed with `https://`), used as
 *                       a secondary so a Vercel deploy always has a working origin.
 *
 * If neither is set:
 *   - In development/test we fall back to `http://localhost:3010` so local dev works.
 *   - In production this is a real misconfiguration. Silently returning
 *     `http://localhost:3010` would bake a broken origin into payment return_urls
 *     and emailed links, so we log loudly (CRITICAL) and throw instead of failing
 *     silently.
 */
export function getBaseUrl(): string {
  const nextAuthUrl = process.env.NEXTAUTH_URL?.trim();
  if (nextAuthUrl) {
    return stripTrailingSlash(nextAuthUrl);
  }

  const vercelUrl = process.env.VERCEL_URL?.trim();
  if (vercelUrl) {
    const withProtocol = /^https?:\/\//.test(vercelUrl) ? vercelUrl : `https://${vercelUrl}`;
    return stripTrailingSlash(withProtocol);
  }

  if (process.env.NODE_ENV === 'production') {
    const error = new Error(
      'getBaseUrl: neither NEXTAUTH_URL nor VERCEL_URL is set in production; ' +
        'cannot build a valid absolute URL and refusing to fall back to localhost'
    );
    trackError(error, ErrorSeverity.CRITICAL, { source: 'base-url' });
    throw error;
  }

  return LOCALHOST_BASE_URL;
}
